import React, { useMemo, useState } from "react";

import centerOfMass from "@turf/center-of-mass";

import { TerritoryLevel } from "@mapmycustomers/shared";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import { Feature, useMap, useMapEventHandler } from "@app/component/map/FeaturedMap";
import colors from "@app/styles/_colors.module.scss";
import defaultIdGetter from "@app/util/defaultIdGetter";
import { DEMOGRAPHY_LABEL_ZINDEX } from "@app/util/map/consts";
import getScaledSize from "@app/util/map/markerStyles/getScaledSize";
import svgToDataUrl from "@app/util/map/svgToDataUrl";

import { DemographicRegionItem } from "..";
import getSvgForLabel from "../../util/getSvgForLabel";

const ZOOM_THRESHOLD = 9;

interface DemographyLabel extends Pick<DemographicRegionItem, "id" | "name"> {
  level: TerritoryLevel;
  position: LongLat;
}

const demographyLabelGeometryGetter = (
  label: DemographyLabel
): Exclude<google.maps.Data.FeatureOptions["geometry"], undefined> =>
  new google.maps.Data.Point({ lat: label.position[1], lng: label.position[0] });

interface Props {
  regionItems: DemographicRegionItem[];
  type: string;
  zIndex?: number;
}

const DemographyLabelFeature: React.FC<Props> = ({
  regionItems,
  type,
  zIndex = DEMOGRAPHY_LABEL_ZINDEX,
}) => {
  const { map } = useMap();

  const demographyLabelStyleGetter = useDynamicCallback(
    (label: DemographyLabel): google.maps.Data.StyleOptions => {
      const color = colors.white;
      const { anchor, size, svg } = getSvgForLabel(label.name, color, 1, {
        textStyles: { textTransform: "uppercase" },
      });
      const scale = 1;
      return {
        clickable: false,
        icon: {
          anchor: new google.maps.Point(...getScaledSize(anchor, scale)),
          scaledSize: new google.maps.Size(...getScaledSize(size, scale)),
          url: svgToDataUrl(svg),
        },
        visible:
          label.level === TerritoryLevel.ADM1 ? zoom < ZOOM_THRESHOLD : zoom > ZOOM_THRESHOLD,
        zIndex,
      };
    }
  );

  const [zoom, setZoom] = useState<number>(map.getZoom()!);
  useMapEventHandler("zoom_changed", () => {
    setZoom(map.getZoom()!);
  });

  const demographyLabels = useMemo(() => {
    return regionItems
      .filter(({ level }) => level !== TerritoryLevel.ADM3)
      .map<DemographyLabel | undefined>(({ id, code, geometry, level, name }) => {
        return {
          id,
          level,
          name:
            level === TerritoryLevel.ADM1 && code ? code : name.replace(/\s*(County)\s*$/gi, ""),
          position: centerOfMass(geometry).geometry.coordinates as LongLat,
        };
      })
      .filter(isDefined);
  }, [regionItems]);

  return (
    <Feature<DemographyLabel>
      geometryGetter={demographyLabelGeometryGetter}
      idGetter={defaultIdGetter}
      items={demographyLabels}
      styleGetter={demographyLabelStyleGetter}
      type={type}
    />
  );
};

export default DemographyLabelFeature;
